
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language, LanguageContextType } from './language/types';
import { translations } from './language/translations';

// Create context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key, // Return key as fallback
});

// Create provider component
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use localStorage to persist language preference if available
  const [language, setLanguage] = useState<Language>('en'); // Default to English
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize language from localStorage or geolocation
  useEffect(() => {
    try {
      // First check if there's a stored language preference
      const storedLanguage = localStorage.getItem('language') as Language;
      if (storedLanguage === 'en' || storedLanguage === 'el') {
        // Use stored language if valid
        setLanguage(storedLanguage);
        setIsInitialized(true);
        return;
      }
      
      // If no stored preference, proceed with default 'en'
      setIsInitialized(true);
      
      // Try to detect user location in the background - don't block rendering
      const checkUserLocation = async () => {
        try {
          const response = await fetch('https://ipapi.co/json/');
          const data = await response.json();
          
          // Set language to Greek if user is from Greece or Cyprus
          if (data.country === 'GR' || data.country === 'CY') {
            setLanguage('el');
          }
        } catch (error) {
          console.error('Error detecting user location:', error);
          // Continue with English if geolocation fails
        }
      };
      
      checkUserLocation();
    } catch (error) {
      console.error("Error initializing language:", error);
      setIsInitialized(true); // Ensure we always set initialized state even on error
    }
  }, []);
  
  // Custom language setter that also triggers an event for external widgets
  const handleSetLanguage = (newLanguage: Language) => {
    setLanguage(newLanguage);
    
    // Dispatch a custom event to notify external scripts about language changes
    const languageChangedEvent = new CustomEvent('language-changed', {
      detail: { language: newLanguage }
    });
    window.dispatchEvent(languageChangedEvent);
  };
  
  // Store language preference when it changes
  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem('language', language);
        
        // Also dispatch the event when language is set from localStorage
        // or geolocation to ensure widgets are updated on first load
        const languageChangedEvent = new CustomEvent('language-changed', {
          detail: { language }
        });
        window.dispatchEvent(languageChangedEvent);
      } catch (error) {
        console.error("Error setting localStorage:", error);
      }
    }
  }, [language, isInitialized]);
  
  // Translation function with safety checks
  const t = (key: string): string => {
    // Check if translations and language exist
    if (!translations || !translations[language]) {
      console.warn(`Missing translations for language: ${language}`);
      return key;
    }
    
    // Check if the key exists in the translations
    const result = translations[language][key];
    
    // Return the translated text or the key if not found
    if (!result) {
      console.warn(`Missing translation for key: ${key} in language: ${language}`);
    }
    return result || key;
  };

  // Render children immediately, don't block on initialization
  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = () => useContext(LanguageContext);

// Re-export types for use in other files
export type { Language, LanguageContextType } from './language/types';
